/**
 * Theme Context
 * Global theme management for dark/light mode
 */

import React, { createContext, useState, useContext, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(true);

  // Load theme preference on mount
  useEffect(() => {
    loadTheme();
  }, []);

  const loadTheme = async () => {
    try {
      const savedTheme = await AsyncStorage.getItem('theme');
      if (savedTheme !== null) {
        setIsDarkMode(savedTheme === 'dark');
      }
    } catch (error) {
      console.error('Error loading theme:', error);
    }
  };

  const toggleTheme = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      await AsyncStorage.setItem('theme', newTheme ? 'dark' : 'light');
    } catch (error) {
      console.error('Error saving theme:', error);
    }
  };

  const theme = {
    dark: {
      background: '#050816',
      cardBackground: 'rgba(26, 31, 58, 0.6)',
      headerBackground: 'rgba(26, 31, 58, 0.8)',
      text: '#FFFFFF',
      textSecondary: '#9CA3AF',
      textTertiary: '#6B7280',
      primary: '#4A90E2',
      success: '#00E676',
      error: '#FF5252',
      warning: '#FF9800',
      border: 'rgba(74, 144, 226, 0.2)',
      tabBar: '#000000',
      tabBarBorder: '#1F2937',
      inputBackground: '#FFFFFF',
      inputText: '#000000',
    },
    light: {
      background: '#F5F5F5',
      cardBackground: '#FFFFFF',
      headerBackground: '#FFFFFF',
      text: '#1F2937',
      textSecondary: '#6B7280',
      textTertiary: '#9CA3AF',
      primary: '#4A90E2',
      success: '#00E676',
      error: '#FF5252',
      warning: '#FF9800',
      border: '#E5E7EB',
      tabBar: '#FFFFFF',
      tabBarBorder: '#E5E7EB',
      inputBackground: '#F9FAFB',
      inputText: '#1F2937',
    },
  };

  const colors = isDarkMode ? theme.dark : theme.light;

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme, colors }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
